import { useRef } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useObserver } from 'mobx-react';
import moment from 'moment';

import { BUNDLES_ROLLOUT_FLAG } from '@dutchie/common/utils/bundles-rollout';
import { specialIsActive } from 'shared/helpers/specials';
import useCart from 'src/hooks/use-cart';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useDispensaryProducts from 'src/dispensary/hooks/use-dispensary-products';
import useDispensarySpecials from 'src/dispensary/hooks/use-dispensary-specials';
import useSpecialMenuCards from 'src/dispensary/hooks/use-special-menu-cards';
import { useHideDiscountFromConsumer } from 'shared/hooks/use-hide-discounts-from-consumer';
import type { Specials } from 'src/gql/graphql';

import type { Bundle } from './bundle.types';
import { buildBundle, getSpecialProductRequirements, specialMatchesCard } from './bundle.utils';

// Re-exported for this file's own test; defined in packages/common/utils/bundles-rollout.ts.
export { BUNDLES_ROLLOUT_FLAG };

export type BundleMembership = {
  bundleId: string;
  bundleName: string;
  productIds: string[];
};

/**
 * Whether the bundle feature should be active for the current dispensary. Shared by useBundles()
 * and by host surfaces that need to exclude native bundle specials from their own rendering once
 * this is on (so they don't double-render).
 */
export function useBundlesEnabled(): boolean {
  const flags = useFlags();
  const cart = useCart();
  // The *viewed* dispensary, not necessarily the cart's -- matches product/index.jsx and
  // use-special-menu-cards.ts, since the two can differ on embedded menus.
  const { dispensary } = useDispensary();
  const cartDispensary = useObserver(() => cart.dispensary);
  // The flag is explicitly configured (on in local/VAPE dev, off elsewhere pending rollout) in
  // every LD environment now, so there's no longer a gap to paper over with a NODE_ENV default --
  // matches every other *_ROLLOUT_FLAG in this app (plain `Boolean(flags[FLAG] ?? false)`).
  const flagEnabled = Boolean(flags[BUNDLES_ROLLOUT_FLAG]);
  // Every other discount surface respects this dispensary-level opt-out; bundles must too.
  const hideDiscounts = useHideDiscountFromConsumer(dispensary ?? cartDispensary);
  return flagEnabled && !hideDiscounts;
}

export function useBundles({ bundleId, skip }: { bundleId?: string; skip?: boolean } = {}): {
  bundles: Bundle[];
  bundleMemberships: BundleMembership[];
  loading: boolean;
} {
  // `skip` lets a conditional caller (e.g. use-bundle-product-ids.ts) avoid mounting the
  // underlying queries at all when it doesn't need bundle data.
  const enabled = useBundlesEnabled() && !skip;
  const cart = useCart();
  const menuType = useObserver(() => cart.menuType);
  // useDispensarySpecials includes preOrderType (derived from this) in its own query variables, so
  // a pickup<->delivery switch can change specials eligibility -- must be part of the cache key too.
  const orderType = useObserver(() => cart.simplifiedOrderType ?? '');
  // Same dispensary resolution as useBundlesEnabled(); only used to key the stale-bundle cache
  // below, not to resolve the specials/products queries (they resolve dispensary internally).
  const { dispensary } = useDispensary();
  const cartDispensary = useObserver(() => cart.dispensary);
  const dispensaryId = dispensary?.id ?? cartDispensary?.id ?? '';
  // Identifies which request a cached `bundles` result belongs to. A bundleId/dispensary/menuType/
  // orderType change must never serve a *different* request's cached bundles while the new one
  // loads -- that's wrong, not just stale. Only a refetch of the SAME request should fall back.
  const cacheKey = `${dispensaryId}:${bundleId ?? ''}:${menuType}:${orderType}`;
  const lastLoadedBundlesRef = useRef<{ key: string; bundles: Bundle[] }>({ key: '', bundles: [] });
  const { specials, loading: specialsLoading } = useDispensarySpecials({
    specialId: bundleId,
    skip: !enabled,
  });
  const { specials: menuCards, loading: cardsLoading } = useSpecialMenuCards({
    specialId: bundleId,
    skip: !enabled,
  });

  // Derived on every render (not memoized) so an Apollo cache update to a nested condition is
  // picked up without a new array reference. Pre-filtered by `discountBundle.enabled` -- the real
  // eligibility gate runs downstream in getSpecialProductRequirements/buildBundle -- AND an
  // active/not-pending check: useDispensarySpecials({ specialId }) deliberately returns an expired
  // or not-yet-started special too (it's fine for an informational detail page), but buildBundle
  // has no date gate of its own, so without this a bookmarked link to an ended special would come
  // back as a priced, "shoppable" bundle.
  const timezone = dispensary?.timezone ?? cartDispensary?.timezone;
  const bundleSpecials = (specials as Specials[]).filter((special) => {
    if (!special.discountBundle?.enabled) {
      return false;
    }
    const isPending = !!special.startStamp && moment().isBefore(special.startStamp);
    return !isPending && specialIsActive(special, timezone);
  });

  // Joined once, reused by both the productIds superset and bundles below, so the
  // menuCards.find() per special doesn't run twice.
  const specialsWithMenuCards = bundleSpecials.map((special) => ({
    special,
    menuCard: menuCards.find((candidate) => specialMatchesCard(special, candidate)),
  }));

  // The superset of every eligible special's resolved product IDs -- used ONLY to size the
  // useDispensaryProducts fetch below. NOT the final bundleMemberships: a special can resolve
  // requirements here and still never become a real Bundle (buildBundle rejects a multi-option
  // product, a product missing a price for this menuType, or one absent from the catalog slice
  // this fetch returns) -- see bundleMemberships, computed from `bundles` further down.
  const productIds = [
    ...new Set(
      specialsWithMenuCards.flatMap(({ special }) =>
        getSpecialProductRequirements(special).map(({ productId }) => productId)
      )
    ),
  ];

  // Known gaps in isolating this query to exactly `productIds`:
  // 1. useDispensaryProducts' underlying query matches local `_id` only, not enterpriseProductId --
  //    a condition resolved ONLY via enterpriseProductId (no local productId) will never be
  //    fetched here, so that bundle silently drops in buildBundle. Enterprise-wide bundles are out
  //    of scope for ECOMM-15145.
  // 2. useDispensaryProducts also ANDs the current page's ambient URL filters onto this query.
  //    page/category/subcategories/strainTypes/brands/terpenes/tags/effects/pricerange/search/
  //    weight ARE genuinely pinned below (types: [] now really overrides the route/?category=
  //    param -- see the fix to use-dispensary-products.js's `hasTypesOverride` check;
  //    skipEffectsFilter/skipPriceRangeFilter/skipSearchFilter/skipWeightFilter opt out of
  //    ?effects=/?pricerange=/?search=/?weight= the same way skipTagsFilter does). Without the
  //    search/weight bypass, a shopper who added a bundled item from a search-results page (or
  //    with a weight filter active) could have the bundle's OTHER required items filtered out of
  //    this query, so buildBundle silently drops the whole bundle -- exactly the surface this PR
  //    adds (cart interstitial), so this isn't just a theoretical page-level gap anymore.
  //    THC/CBD potency range still has no override in the `filters` API -- same silent-drop risk,
  //    just lower traffic (potency range filters are rarer than search/weight).
  const { products, loading: productsLoading } = useDispensaryProducts({
    page: 1,
    filters: {
      productIds,
      useCache: true,
      types: [],
      subcategories: [],
      strainTypes: [],
      brands: [],
      terpenes: [],
      skipTagsFilter: true,
      skipSmartTagsFilter: true,
      skipEffectsFilter: true,
      skipPriceRangeFilter: true,
      skipSearchFilter: true,
      skipWeightFilter: true,
    },
    perPage: Math.max(productIds.length, 1),
    skip: !enabled || productIds.length === 0,
  });

  // Not memoized: specialsWithMenuCards/products are new array/object references every render
  // by design (see the comment above bundleSpecials), so a useMemo here would recompute on every
  // render regardless. `bundles`/`visibleBundles` get a new array identity every render -- don't
  // put them in a useEffect/useMemo dependency list expecting referential stability.
  const bundles = specialsWithMenuCards.flatMap(({ special, menuCard }) => {
    const bundle = buildBundle({ special, menuCard, products, menuType });
    return bundle ? [bundle] : [];
  });

  const loading = enabled && (specialsLoading || cardsLoading || (productIds.length > 0 && productsLoading));
  // Written during render rather than a useEffect: the fallback below needs this updated on the
  // SAME render that first produces a non-empty `bundles`, or a refetch's first empty render
  // would flash empty before an effect could catch up. Safe because the write is idempotent for
  // this render's own inputs, and gated by cacheKey -- a write from a request that's no longer
  // current can never be read back by the check below.
  if (bundles.length > 0) {
    lastLoadedBundlesRef.current = { key: cacheKey, bundles };
  } else if (!loading) {
    // A settled (not mid-refetch) empty result means nothing is eligible for THIS exact request --
    // clear any previously cached bundles under the same key, or a LATER refetch's loading render
    // (the fallback below) could resurrect a bundle that's since been removed/expired.
    lastLoadedBundlesRef.current = { key: cacheKey, bundles: [] };
  }
  // While refetching the SAME request, keep showing the last successfully built set instead of
  // flashing empty. Doesn't apply across a cacheKey mismatch (wrong, not just stale), or before
  // anything has loaded once (empty is simply "nothing yet").
  let visibleBundles = bundles;
  if (bundles.length === 0 && loading && lastLoadedBundlesRef.current.key === cacheKey) {
    visibleBundles = lastLoadedBundlesRef.current.bundles;
  }

  // Built from `visibleBundles` (what the hook actually returns), NOT the raw `bundles` -- during
  // the same-key stale-fallback above, `bundles` can be momentarily empty while `visibleBundles`
  // still shows the last successfully built set. Memberships must track whichever bundles are
  // actually visible, or a memberships consumer (e.g. product-bundle-offers, ECOMM-15148) would
  // flash "no bundle" on every poll tick for a bundle that's still on screen.
  const bundleMemberships: BundleMembership[] = visibleBundles.map((bundle) => ({
    bundleId: bundle.id,
    bundleName: bundle.name,
    productIds: bundle.items.map((item) => item.productId),
  }));

  return {
    bundles: visibleBundles,
    bundleMemberships,
    loading,
  };
}
